import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DrawerProvider, Button } from 'equal-ds-ui';
import type { TableColumn } from 'equal-ds-ui';
import { Download, RefreshCw, UploadCloud, UserPlus } from 'lucide-react';
import { useAdminUsers } from './hooks/useAdminUsers';
import { useAdminUserPermissions } from './hooks/useAdminUserPermissions';
import { AdminUsersTable } from './AdminUsersTable';
import { AdminUserFormDrawer } from './AdminUserFormDrawer';
import { AdminUsersBulkUploadDrawer } from './AdminUsersBulkUploadDrawer';
import { AdminUserStatusDialog } from './AdminUserStatusDialog';
import { AdminUserResetPasswordDialog } from './AdminUserResetPasswordDialog';
import { AdminUserApprovalDialog } from './AdminUserApprovalDialog';
import { SignupRequestApprovalDialog } from './SignupRequestApprovalDialog';
import { adminUsersAPI } from '../../../services/admin-users-api.service';
import type { SignupRequest } from '../../../types/signup-requests';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { Alert } from '../../ui/Alert';
import type {
  AdminUser,
  AdminUserFormValues,
  AdminUsersPageProps,
  AdminUserTableRow,
  AdminRole,
  BulkUploadUserRow,
  AdminUserSearchField,
} from '../../../types/admin-users';

const CSV_HEADERS = [
  'Name',
  'Mobile',
  'Email',
  'Timestamp',
  'UserCreatedBy',
  'Employee ID',
  'Admin',
  'Role',
  'Account Status',
  'Business Department Code',
  'Department/ Division name',
];

const BULK_NAME_REGEX = /^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/;
const BULK_MOBILE_REGEX = /^[6-9]\d{9}$/;
const BULK_EMP_ID_REGEX = /^[a-zA-Z0-9]+$/;
const BULK_EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
// Password: min 8 chars, at least one lowercase, uppercase, digit, and special char (@$!%*?&), no spaces
const BULK_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@$!%*?&])(?!.*\s).{8,}$/;
// Alphanumeric only (for demand_num, dept_code)
const BULK_ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]+$/;
// Alphanumeric with spaces (for dept_owner)
const BULK_ALPHANUMERIC_SPACE_REGEX = /^[a-zA-Z0-9\s]+$/;

/** Minimum duration to show loading spinner for better UX (prevents flash) */
const MIN_LOADING_DURATION_MS = 300;

/**
 * Field validation rule for schema-based validation
 */
interface FieldValidationRule {
  key: keyof BulkUploadUserRow;
  label: string;
  required: 'always' | 'business' | 'loanOfficer' | 'never';
  pattern?: RegExp;
  patternError?: string;
  isEmail?: boolean; // Special handling for email domain validation
  isArray?: boolean; // For purpose and channel_type arrays
}

/**
 * Validation schema for bulk upload fields
 * This replaces the sequential if/return blocks with a declarative approach
 */
const BULK_VALIDATION_SCHEMA: FieldValidationRule[] = [
  // Basic fields - always required
  {
    key: 'name',
    label: 'Name',
    required: 'always',
    pattern: BULK_NAME_REGEX,
    patternError: 'Invalid name. Only letters, numbers, and single spaces allowed.',
  },
  {
    key: 'mobile',
    label: 'Mobile',
    required: 'always',
    pattern: BULK_MOBILE_REGEX,
    patternError: 'Invalid mobile number. Must be 10 digits starting with 6-9.',
  },
  {
    key: 'email',
    label: 'Email',
    required: 'always',
    pattern: BULK_EMAIL_REGEX,
    patternError: 'Invalid email address.',
    isEmail: true,
  },
  {
    key: 'emp_id',
    label: 'Employee ID',
    required: 'always',
    pattern: BULK_EMP_ID_REGEX,
    patternError: 'Invalid employee ID. Only alphanumeric characters allowed.',
  },
  {
    key: 'password',
    label: 'Password',
    required: 'always',
    pattern: BULK_PASSWORD_REGEX,
    patternError: 'Invalid password. Must be 8+ chars with uppercase, lowercase, number, and special char (@$!%*?&).',
  },
  // Business fields - required for role 2 and above
  {
    key: 'division',
    label: 'Department Name',
    required: 'business',
  },
  {
    key: 'dept_code',
    label: 'Business Department Code',
    required: 'business',
    pattern: BULK_ALPHANUMERIC_REGEX,
    patternError: 'Invalid department code. Only alphanumeric characters allowed.',
  },
  {
    key: 'dept_owner',
    label: 'Business Department Owner',
    required: 'business',
    pattern: BULK_ALPHANUMERIC_SPACE_REGEX,
    patternError: 'Invalid department owner. Only alphanumeric characters and spaces allowed.',
  },
  {
    key: 'email_business_owner',
    label: 'Email Id of Business Owner',
    required: 'business',
    pattern: BULK_EMAIL_REGEX,
    patternError: 'Invalid business owner email address.',
    isEmail: true,
  },
  // Loan Officer fields - required for role 3+
  {
    key: 'demand_num',
    label: 'Demand No',
    required: 'loanOfficer',
    pattern: BULK_ALPHANUMERIC_REGEX,
    patternError: 'Invalid demand number. Only alphanumeric characters allowed.',
  },
  {
    key: 'purpose',
    label: 'Purpose Details',
    required: 'loanOfficer',
    isArray: true,
  },
  {
    key: 'channel_type',
    label: 'Channel Type',
    required: 'loanOfficer',
    isArray: true,
  },
];

/**
 * Derive role requirement level from roles array
 * This makes role IDs dynamic based on backend data instead of hardcoded values
 *
 * @returns Object with admin and businessRole IDs derived from roles data
 */
function deriveRoleRequirements(roles: AdminRole[]): {
  adminRoleIds: Set<string>;
  businessRoleIds: Set<string>;
} {
  const adminRoleIds = new Set<string>();
  const businessRoleIds = new Set<string>();

  roles.forEach((role) => {
    const roleName = role.role_name?.toLowerCase() ?? '';
    const roleId = String(role.id);

    // Admin roles - only basic fields required
    if (roleName === 'admin' || roleName === 'superadmin') {
      adminRoleIds.add(roleId);
    }
    // Role 2 type roles - business fields required but not loan officer fields
    // Common patterns: role_name contains "manager", "supervisor", or specific IDs
    else if (roleName.includes('manager') || roleName.includes('supervisor') || roleId === '2') {
      businessRoleIds.add(roleId);
    }
  });

  // Fallback: if no admin role found, use ID '1' as default
  if (adminRoleIds.size === 0) {
    adminRoleIds.add('1');
  }
  // Fallback: if no business role found, use ID '2' as default
  if (businessRoleIds.size === 0) {
    businessRoleIds.add('2');
  }

  return { adminRoleIds, businessRoleIds };
}

/**
 * Get allowed email domains from sessionStorage (if configured)
 * Returns null if domain validation is not enabled
 */
function getAllowedEmailDomains(): string[] | null {
  try {
    const encoded = sessionStorage.getItem('loggedUserInfo');
    if (!encoded) return null;
    const decoded = atob(encoded);
    const parsed = JSON.parse(decoded);
    // Check if domain validation is enabled and domains are configured
    const domains = parsed?.allowed_domains || parsed?.email_domains;
    if (Array.isArray(domains) && domains.length > 0) {
      return domains.map((d: string) => d.toLowerCase().trim());
    }
    return null;
  } catch (error) {
    // Log warning so misconfigurations don't go unnoticed during development
    if (import.meta.env.DEV) {
      console.warn('[AdminUsersPage] Failed to parse allowed email domains from sessionStorage:', error);
    }
    return null;
  }
}

/**
 * Validate email domain against whitelist (if configured)
 */
function isEmailDomainAllowed(email: string, allowedDomains: string[] | null): boolean {
  if (!allowedDomains) return true; // No domain restriction
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return false;
  return allowedDomains.some((allowed) => domain === allowed || domain.endsWith(`.${allowed}`));
}

function buildCsv(users: AdminUser[]): string {
  const replacer = (_key: string, value: any) => (value === null || value === undefined ? '' : value);

  const rows = users.map((user) => {
    const profile = user.user_profile ?? ({} as AdminUser['user_profile']);
    const role = profile.roles ?? ({} as AdminUser['user_profile']['roles']);
    const createdAt = profile.createdAt ? new Date(profile.createdAt) : null;
    const formattedDate = createdAt && !Number.isNaN(createdAt.getTime())
      ? new Intl.DateTimeFormat('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }).format(createdAt)
      : profile.createdAt ?? '';

    const isAdmin =
      role?.display_name?.toLowerCase() === 'admin' ||
      role?.role_name?.toLowerCase() === 'admin';

    return {
      Name: profile.name ?? '',
      Mobile: profile.mobile ?? '',
      Email: user.email,
      Timestamp: formattedDate,
      UserCreatedBy: profile.created_by ?? '',
      'Employee ID': profile.emp_id ?? '',
      Admin: isAdmin ? 'Yes' : 'No',
      Role: role?.display_name ?? role?.role_name ?? '',
      'Account Status': user.status ?? '',
      'Business Department Code': profile.dept_code ?? '',
      'Department/ Division name': profile.division ?? '',
    };
  });

  const csvRows = [
    CSV_HEADERS.join(','),
    ...rows.map((row) =>
      CSV_HEADERS.map((header) => JSON.stringify((row as any)[header], replacer)).join(',')
    ),
  ];

  return csvRows.join('\r\n');
}

const BULK_HEADER_MAP: Record<string, keyof BulkUploadUserRow> = {
  Role: 'user_role_id',
  Name: 'name',
  'Mobile Number': 'mobile',
  Email: 'email',
  'Employee ID': 'emp_id',
  'Department Name': 'division',
  'Business Department Code': 'dept_code',
  'Business Department Owner': 'dept_owner',
  'Demand No': 'demand_num',
  'Email Id of Business Owner': 'email_business_owner',
  Password: 'password',
  'Purpose Details': 'purpose',
  'Channel Type': 'channel_type',
};

function parseBulkCsv(content: string): BulkUploadUserRow[] {
  const lines = content
    .split('\n')
    .map((line) => line.replace(/\r/g, '').trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return [];
  }

  const headers = lines[0].split(',').map((header) => header.trim());
  const mappedHeaders = headers.map((header) => BULK_HEADER_MAP[header] ?? null);

  if (mappedHeaders.some((header) => header === null)) {
    throw new Error('Invalid CSV headers. Please use the provided sample template.');
  }

  const rows: BulkUploadUserRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const columns = lines[i].split(',');
    if (columns.length === 1 && columns[0].trim() === '') {
      continue;
    }

    const row: Record<string, any> = {};

    mappedHeaders.forEach((header, index) => {
      if (!header) {
        return;
      }
      const rawValue = columns[index] ?? '';
      row[header] = rawValue.trim();
    });

    if (typeof row.purpose === 'string') {
      row.purpose = row.purpose ? row.purpose.split(/[;\s]+/).filter(Boolean) : [];
    }

    if (typeof row.channel_type === 'string') {
      row.channel_type = row.channel_type ? row.channel_type.split(/[;\s]+/).filter(Boolean) : [];
    }

    rows.push(row as BulkUploadUserRow);
  }

  return rows;
}

/**
 * Check for duplicate values within the uploaded file
 * @returns Map of field -> Set of duplicate values
 */
function findWithinFileDuplicates(
  rows: BulkUploadUserRow[]
): { emailDuplicates: Set<string>; mobileDuplicates: Set<string>; empIdDuplicates: Set<string> } {
  const emailCount = new Map<string, number>();
  const mobileCount = new Map<string, number>();
  const empIdCount = new Map<string, number>();

  // Count occurrences
  rows.forEach((row) => {
    const email = row.email?.toLowerCase().trim();
    const mobile = row.mobile?.trim();
    const empId = row.emp_id?.trim();

    if (email) {
      emailCount.set(email, (emailCount.get(email) ?? 0) + 1);
    }
    if (mobile) {
      mobileCount.set(mobile, (mobileCount.get(mobile) ?? 0) + 1);
    }
    if (empId) {
      empIdCount.set(empId, (empIdCount.get(empId) ?? 0) + 1);
    }
  });

  // Find duplicates (count > 1)
  const emailDuplicates = new Set<string>();
  const mobileDuplicates = new Set<string>();
  const empIdDuplicates = new Set<string>();

  emailCount.forEach((count, email) => {
    if (count > 1) emailDuplicates.add(email);
  });
  mobileCount.forEach((count, mobile) => {
    if (count > 1) mobileDuplicates.add(mobile);
  });
  empIdCount.forEach((count, empId) => {
    if (count > 1) empIdDuplicates.add(empId);
  });

  return { emailDuplicates, mobileDuplicates, empIdDuplicates };
}

/**
 * Check if email/mobile/emp_id already exists in the system via API
 * Uses batched API calls to avoid overwhelming the backend
 * @returns Set of values that already exist + flag indicating if any API calls failed
 */
async function checkSystemDuplicates(
  rows: BulkUploadUserRow[]
): Promise<{
  existingEmails: Set<string>;
  existingMobiles: Set<string>;
  existingEmpIds: Set<string>;
  apiCheckFailed: boolean;
}> {
  const existingEmails = new Set<string>();
  const existingMobiles = new Set<string>();
  const existingEmpIds = new Set<string>();
  let apiCheckFailed = false;

  // Get unique values to check
  const uniqueEmails = [...new Set(rows.map(r => r.email?.toLowerCase().trim()).filter(Boolean))];
  const uniqueMobiles = [...new Set(rows.map(r => r.mobile?.trim()).filter(Boolean))];
  const uniqueEmpIds = [...new Set(rows.map(r => r.emp_id?.trim()).filter(Boolean))];

  // Batch size for concurrent API calls to avoid overwhelming the backend
  const BATCH_SIZE = 10;

  /**
   * Process values in batches with concurrency limiting
   */
  async function checkInBatches(
    values: string[],
    keyType: 'email' | 'mobile' | 'emp_id',
    resultSet: Set<string>
  ): Promise<void> {
    for (let i = 0; i < values.length; i += BATCH_SIZE) {
      const batch = values.slice(i, i + BATCH_SIZE);
      const batchPromises = batch.map(value =>
        adminUsersAPI.checkKeyExistence({ keyType, keyValue: value })
          .then((response) => {
            if (response.message === 'key exist') {
              resultSet.add(value);
            }
          })
          .catch(() => {
            // Track that at least one API call failed
            apiCheckFailed = true;
          })
      );
      // Wait for current batch to complete before starting next batch
      await Promise.all(batchPromises);
    }
  }

  // Check each field type in batches (sequentially between types, parallel within batches)
  await checkInBatches(uniqueEmails, 'email', existingEmails);
  await checkInBatches(uniqueMobiles, 'mobile', existingMobiles);
  await checkInBatches(uniqueEmpIds, 'emp_id', existingEmpIds);

  return { existingEmails, existingMobiles, existingEmpIds, apiCheckFailed };
}

/**
 * Schema-based validation for bulk upload rows
 * Replaces sequential if/return blocks with a declarative, maintainable approach
 * Now async to support system duplicate checking via API
 */
async function validateBulkRows(
  rows: BulkUploadUserRow[],
  roles: AdminRole[]
): Promise<{ valid: BulkUploadUserRow[]; invalid: BulkUploadUserRow[]; apiCheckFailed: boolean }> {
  const valid: BulkUploadUserRow[] = [];
  const invalid: BulkUploadUserRow[] = [];

  // Get allowed email domains (if configured)
  const allowedDomains = getAllowedEmailDomains();

  // Build role ID lookup map
  const roleIdMap = new Map<string, string>();
  roles.forEach((role) => {
    roleIdMap.set(String(role.id), String(role.id));
    if (role.display_name) {
      roleIdMap.set(role.display_name.toLowerCase(), String(role.id));
    }
    if (role.role_name) {
      roleIdMap.set(role.role_name.toLowerCase(), String(role.id));
    }
  });

  // Derive role requirements dynamically from roles data
  const { adminRoleIds, businessRoleIds } = deriveRoleRequirements(roles);

  // Step 1: Find within-file duplicates
  const { emailDuplicates, mobileDuplicates, empIdDuplicates } = findWithinFileDuplicates(rows);

  // Step 2: Check system duplicates via API
  const { existingEmails, existingMobiles, existingEmpIds, apiCheckFailed } = await checkSystemDuplicates(rows);

  /**
   * Validate a single field against its rule
   * @returns Error message if validation fails, null if passes
   */
  const validateField = (
    row: BulkUploadUserRow,
    rule: FieldValidationRule,
    isRequired: boolean,
    allowedDomains: string[] | null
  ): string | null => {
    const rawValue = row[rule.key];

    // Handle array fields (purpose, channel_type)
    if (rule.isArray) {
      const arrValue = Array.isArray(rawValue) ? rawValue : [];
      if (isRequired && arrValue.length === 0) {
        return `${rule.label} is required for this role.`;
      }
      return null; // Arrays don't have pattern validation
    }

    // Handle string fields
    const value = typeof rawValue === 'string' ? rawValue.trim() : '';

    // Check required
    if (isRequired && !value) {
      return `${rule.label} is required for this role.`;
    }

    // If optional and empty, skip pattern validation
    if (!value) {
      return null;
    }

    // Pattern validation
    if (rule.pattern && !rule.pattern.test(value)) {
      return rule.patternError ?? `Invalid ${rule.label.toLowerCase()}.`;
    }

    // Email domain validation
    if (rule.isEmail && !isEmailDomainAllowed(value, allowedDomains)) {
      return rule.key === 'email'
        ? 'Email domain not allowed. Please use an authorized domain.'
        : `${rule.label} domain not allowed.`;
    }

    return null;
  };

  // Process each row
  rows.forEach((row) => {
    const normalizedRow: BulkUploadUserRow = {
      ...row,
      email: row.email?.toLowerCase() ?? '',
      email_business_owner: row.email_business_owner?.toLowerCase() ?? '',
      purpose: Array.isArray(row.purpose) ? row.purpose : [],
      channel_type: Array.isArray(row.channel_type) ? row.channel_type : [],
    };

    const email = normalizedRow.email?.trim();
    const mobile = normalizedRow.mobile?.trim();
    const empId = normalizedRow.emp_id?.trim();

    // Check for within-file duplicates first
    if (email && emailDuplicates.has(email)) {
      normalizedRow.userStatus = `Duplicate email "${email}" found in file. Each email must be unique.`;
      invalid.push(normalizedRow);
      return;
    }

    if (mobile && mobileDuplicates.has(mobile)) {
      normalizedRow.userStatus = `Duplicate mobile "${mobile}" found in file. Each mobile must be unique.`;
      invalid.push(normalizedRow);
      return;
    }

    if (empId && empIdDuplicates.has(empId)) {
      normalizedRow.userStatus = `Duplicate Employee ID "${empId}" found in file. Each Employee ID must be unique.`;
      invalid.push(normalizedRow);
      return;
    }

    // Check for system duplicates (already exists in database)
    if (email && existingEmails.has(email)) {
      normalizedRow.userStatus = `Email "${email}" already exists in the system.`;
      invalid.push(normalizedRow);
      return;
    }

    if (mobile && existingMobiles.has(mobile)) {
      normalizedRow.userStatus = `Mobile "${mobile}" already exists in the system.`;
      invalid.push(normalizedRow);
      return;
    }

    if (empId && existingEmpIds.has(empId)) {
      normalizedRow.userStatus = `Employee ID "${empId}" already exists in the system.`;
      invalid.push(normalizedRow);
      return;
    }

    // 1. Role validation (must be done first to determine field requirements)
    const roleCandidate = row.user_role_id?.toString().trim().toLowerCase();
    const resolvedRoleId = roleCandidate ? roleIdMap.get(roleCandidate) : undefined;

    if (!resolvedRoleId) {
      normalizedRow.userStatus = 'Unknown role. Please verify role name in template.';
      invalid.push(normalizedRow);
      return;
    }

    normalizedRow.user_role_id = resolvedRoleId;

    // Determine role-based requirements using dynamic role detection
    const isAdmin = adminRoleIds.has(resolvedRoleId);
    const isBusinessRole = businessRoleIds.has(resolvedRoleId);
    const isLoanOfficer = !isAdmin && !isBusinessRole; // All other roles
    const requiresBusinessFields = isBusinessRole || isLoanOfficer;

    // 2. Validate all fields using the schema
    for (const rule of BULK_VALIDATION_SCHEMA) {
      // Determine if this field is required for this role
      let isRequired = false;
      switch (rule.required) {
        case 'always':
          isRequired = true;
          break;
        case 'business':
          isRequired = requiresBusinessFields;
          break;
        case 'loanOfficer':
          isRequired = isLoanOfficer;
          break;
        case 'never':
          isRequired = false;
          break;
      }

      const error = validateField(normalizedRow, rule, isRequired, allowedDomains);
      if (error) {
        normalizedRow.userStatus = error;
        invalid.push(normalizedRow);
        return;
      }
    }

    // All validations passed
    valid.push(normalizedRow);
  });

  return { valid, invalid, apiCheckFailed };
}

function getCurrentUserId(): string | null {
  try {
    const encoded = sessionStorage.getItem('loggedUserInfo');
    if (!encoded) {
      return null;
    }
    const decoded = atob(encoded);
    const parsed = JSON.parse(decoded);
    return parsed?.id ?? parsed?.user_profile?.user_id ?? null;
  } catch (error) {
    console.warn('[AdminUsersPage] Unable to resolve current user id:', error);
    return null;
  }
}

export const AdminUsersPage: React.FC<AdminUsersPageProps> = () => {
  const { handleError, errorMessage, clearError } = useErrorHandler();
  const permissions = useAdminUserPermissions();
  const {
    isLoading,
    rows,
    pagination,
    searchForm,
    setSearchField,
    setSearchValue,
    applySearch,
    clearSearch,
    refresh,
    setPage,
    setPageSize,
  } = useAdminUsers();

  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [statusDialog, setStatusDialog] = useState<{
    user: AdminUserTableRow | null;
    nextStatus: string;
  }>({ user: null, nextStatus: '' });
  const [resetDialogUser, setResetDialogUser] = useState<AdminUserTableRow | null>(null);
  const [approvalDialog, setApprovalDialog] = useState<{
    user: AdminUserTableRow | null;
    action: 'APPROVE' | 'REJECT';
  }>({ user: null, action: 'APPROVE' });
  // Signup request approval dialog (for PENDING_APPROVAL users without role)
  const [signupApprovalDialog, setSignupApprovalDialog] = useState<{
    request: SignupRequest | null;
    mode: 'approve' | 'reject';
  }>({ request: null, mode: 'approve' });
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formProcessing, setFormProcessing] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [statusProcessing, setStatusProcessing] = useState(false);
  const [resetProcessing, setResetProcessing] = useState(false);
  const [approvalProcessing, setApprovalProcessing] = useState(false);
  const [exportProcessing, setExportProcessing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkCsvRows, setBulkCsvRows] = useState<BulkUploadUserRow[]>([]);
  const [bulkValidRows, setBulkValidRows] = useState<BulkUploadUserRow[]>([]);
  const [bulkInvalidRows, setBulkInvalidRows] = useState<BulkUploadUserRow[]>([]);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkRoles, setBulkRoles] = useState<AdminRole[]>([]);

  useEffect(() => {
    if (!bulkDrawerOpen) {
      setBulkCsvRows([]);
      setBulkValidRows([]);
      setBulkInvalidRows([]);
      setBulkError(null);
      setBulkProcessing(false);
    }
  }, [bulkDrawerOpen]);

  // Auto-dismiss success message after 4 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      // Run refresh and minimum delay in parallel to prevent UI flicker
      await Promise.all([
        refresh(),
        new Promise((resolve) => setTimeout(resolve, MIN_LOADING_DURATION_MS)),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [refresh]);

  const handleCreateClick = useCallback(() => {
    setFormMode('create');
    setEditingUser(null);
    setFormDrawerOpen(true);
  }, []);

  const handleEdit = useCallback(async (row: AdminUserTableRow) => {
    // Try multiple locations for user_id (different data sources store it differently)
    const userId = row.raw?.user_profile?.user_id
      ?? row.raw?.user_profile?.id
      ?? row.raw?.id
      ?? row.id;

    if (!userId) {
      handleError(new Error('Unable to determine user ID for edit.'), {
        component: 'AdminUsersPage',
        action: 'editUser',
      });
      return;
    }

    try {
      setEditLoading(true);
      // Fetch fresh user data from API (matching Angular's editUserDialog flow)
      const userData = await adminUsersAPI.getUserById(String(userId));

      if (!userData) {
        handleError(new Error('User not found.'), {
          component: 'AdminUsersPage',
          action: 'editUser',
        });
        return;
      }

      setFormMode('edit');
      setEditingUser(userData);
      setFormDrawerOpen(true);
    } catch (error) {
      handleError(error, {
        component: 'AdminUsersPage',
        action: 'editUser',
      });
    } finally {
      setEditLoading(false);
    }
  }, [handleError]);

  const handleStatusChange = useCallback((row: AdminUserTableRow) => {
    setStatusDialog({
      user: row,
      nextStatus: row.nextStatus,
    });
  }, []);

  const handleResetPassword = useCallback((row: AdminUserTableRow) => {
    // Check if user is admin/superadmin - admins cannot reset other admin passwords
    // Use role_name (not display_name) to match Angular behavior exactly
    // Angular: element.user_profile.roles.role_name == 'superadmin' || element.user_profile.roles.role_name == 'admin'
    const PROTECTED_ROLES = new Set(['admin', 'superadmin']);
    const rawRoleName = (row.raw?.user_profile?.roles?.role_name ?? '').toLowerCase();
    const isProtectedRole = PROTECTED_ROLES.has(rawRoleName);

    if (isProtectedRole) {
      handleError(
        new Error(`Cannot reset password for ${row.name}. Admin users' passwords cannot be reset by other admins.`),
        { component: 'AdminUsersPage', action: 'resetPassword' }
      );
      return;
    }

    setResetDialogUser(row);
  }, [handleError]);

  /**
   * Check if a user is a signup request (PENDING_APPROVAL without assigned role)
   * Signup requests need role assignment during approval via SignupRequestApprovalDialog
   */
  const isSignupRequest = useCallback((row: AdminUserTableRow): boolean => {
    // Signup requests have PENDING_APPROVAL status and _isSignupRequest flag
    const isPending = row.status === 'PENDING_APPROVAL';
    const hasSignupFlag = (row.raw as any)?._isSignupRequest === true;
    return isPending && hasSignupFlag;
  }, []);

  /**
   * Convert AdminUserTableRow to SignupRequest format for the approval dialog
   */
  const convertToSignupRequest = useCallback((row: AdminUserTableRow): SignupRequest => {
    const profile = row.raw?.user_profile;
    // Use row.id (which comes from request.id via mapSignupRequestToRow) as primary source
    // Fall back to profile.user_id or raw.id if available
    const requestId = Number(row.id) || Number(profile?.user_id) || Number((row.raw as any)?.id) || 0;
    return {
      id: requestId,
      email: row.email,
      status: 'PENDING_APPROVAL',
      createdAt: profile?.createdAt || new Date().toISOString(),
      profile: {
        emp_id: String(profile?.emp_id || row.empId || ''),
        name: profile?.name || row.name || '',
        mobile: profile?.mobile || row.mobile || '',
        // Map purpose field to reason_for_request if available
        reason_for_request: Array.isArray(profile?.purpose)
          ? profile.purpose.join(', ')
          : (profile as any)?.reason_for_request || (profile?.purpose as any) || 'Not provided',
      },
    };
  }, []);

  const handleApprove = useCallback((row: AdminUserTableRow) => {
    if (isSignupRequest(row)) {
      // Use SignupRequestApprovalDialog for signup requests (needs role assignment)
      setSignupApprovalDialog({
        request: convertToSignupRequest(row),
        mode: 'approve',
      });
    } else {
      // Use regular AdminUserApprovalDialog for existing users
      setApprovalDialog({ user: row, action: 'APPROVE' });
    }
  }, [isSignupRequest, convertToSignupRequest]);

  const handleReject = useCallback((row: AdminUserTableRow) => {
    if (isSignupRequest(row)) {
      // Use SignupRequestApprovalDialog for signup requests
      setSignupApprovalDialog({
        request: convertToSignupRequest(row),
        mode: 'reject',
      });
    } else {
      // Use regular AdminUserApprovalDialog for existing users
      setApprovalDialog({ user: row, action: 'REJECT' });
    }
  }, [isSignupRequest, convertToSignupRequest]);

  const handleDownloadCsv = useCallback(async () => {
    try {
      setExportProcessing(true);
      const response = await adminUsersAPI.getAllUsers();
      const users = response?.data?.rows ?? [];
      if (users.length === 0) {
        setSuccessMessage('No users available to export.');
        return;
      }

      const csvContent = buildCsv(users);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'admin_users.csv';
      link.click();
      window.URL.revokeObjectURL(url);

      setSuccessMessage('Export started. Check your downloads folder.');
    } catch (error) {
      handleError(error, { component: 'AdminUsersPage', action: 'downloadCsv' });
    } finally {
      setExportProcessing(false);
    }
  }, [handleError]);

  const handleSearchSubmit = useCallback(
    (value: string, field: AdminUserSearchField) => {
      applySearch({ searchValue: value, searchField: field });
    },
    [applySearch]
  );

  const handleSearchReset = useCallback(() => {
    clearSearch();
  }, [clearSearch]);

  const handleFormSubmit = useCallback(
    async (values: AdminUserFormValues) => {
      const trim = (input?: string) => (input ?? '').trim();
      const sanitizeArray = (arr: unknown[]) =>
        Array.from(new Set((arr ?? []).map((item) => String(item ?? '').trim()).filter(Boolean)));

      try {
        setFormProcessing(true);
        if (formMode === 'create') {
          await adminUsersAPI.createUser({
            name: trim(values.name),
            mobile: trim(values.mobile),
            email: trim(values.email).toLowerCase(),
            emp_id: trim(values.emp_id),
            user_role_id: String(values.user_role_id),
            is_superadmin: false, // Always false for admin-created users (matching Angular)
            division: trim(values.division),
            dept_owner: trim(values.dept_owner),
            dept_code: trim(values.dept_code),
            demand_num: trim(values.demand_num),
            email_business_owner: trim(values.email_business_owner).toLowerCase(),
            purpose: sanitizeArray(values.purpose),
            channel_type: sanitizeArray(values.channel_type),
            password: trim(values.password) || undefined,
          });
          setSuccessMessage('User created successfully.');
        } else {
          const userId = editingUser?.user_profile?.user_id;
          if (!userId) {
            throw new Error('Unable to determine user ID for update.');
          }
          await adminUsersAPI.updateUser({
            user_id: String(userId),
            name: trim(values.name),
            mobile: trim(values.mobile),
            emp_id: trim(values.emp_id),
            user_role_id: String(values.user_role_id),
            is_superadmin: false, // Always false for admin-updated users (matching Angular)
            division: trim(values.division),
            dept_owner: trim(values.dept_owner),
            dept_code: trim(values.dept_code),
            demand_num: trim(values.demand_num),
            email_business_owner: trim(values.email_business_owner).toLowerCase(),
            purpose: sanitizeArray(values.purpose),
            channel_type: sanitizeArray(values.channel_type),
          });
          setSuccessMessage('User updated successfully.');
        }

        setFormDrawerOpen(false);
        await refresh();
      } catch (error) {
        handleError(error, {
          component: 'AdminUsersPage',
          action: formMode === 'create' ? 'createUser' : 'updateUser',
        });
      } finally {
        setFormProcessing(false);
      }
    },
    [editingUser, formMode, handleError, refresh]
  );

  const closeStatusDialog = () => setStatusDialog({ user: null, nextStatus: '' });
  const closeResetDialog = () => setResetDialogUser(null);
  const closeApprovalDialog = () =>
    setApprovalDialog({ user: null, action: approvalDialog.action });
  const closeSignupApprovalDialog = () =>
    setSignupApprovalDialog({ request: null, mode: signupApprovalDialog.mode });

  const handleStatusConfirm = useCallback(async () => {
    if (!statusDialog.user) {
      console.error('[AdminUsersPage] handleStatusConfirm: No user in statusDialog');
      return;
    }

    // Try multiple locations for user_id (matching Angular's user.user_profile.user_id)
    const raw = statusDialog.user.raw;
    const userId = raw?.user_profile?.user_id
      ?? raw?.user_profile?.id
      ?? (raw as any)?.id
      ?? statusDialog.user.id;

    if (!userId) {
      handleError(new Error('Missing user identifier for status change.'), {
        component: 'AdminUsersPage',
        action: 'updateUserStatus',
      });
      return;
    }

    try {
      setStatusProcessing(true);
      await adminUsersAPI.updateUserStatus({
        user_id: String(userId),
        user_status: statusDialog.nextStatus,
      });
      setSuccessMessage(`User status updated to ${statusDialog.nextStatus}.`);
      closeStatusDialog();
      await refresh();
    } catch (error) {
      handleError(error, {
        component: 'AdminUsersPage',
        action: 'updateUserStatus',
      });
    } finally {
      setStatusProcessing(false);
    }
  }, [handleError, refresh, statusDialog]);

  const handleResetPasswordConfirm = useCallback(async (password: string) => {
    if (!resetDialogUser) {
      return;
    }

    try {
      setResetProcessing(true);
      // Call reset_password API with payload matching Angular implementation
      await adminUsersAPI.resetPassword({
        type: 'change',
        email: resetDialogUser.email,
        password: password,
      });
      // Success message is shown inside the dialog, which will auto-close
      // Don't close dialog here - let the dialog handle it after showing success
    } catch (error) {
      // Re-throw so the dialog can catch and display the error
      throw error;
    } finally {
      setResetProcessing(false);
    }
  }, [resetDialogUser]);

  const handleApprovalConfirm = useCallback(async () => {
    if (!approvalDialog.user) {
      return;
    }

    const userId = approvalDialog.user.raw.user_profile?.user_id;
    if (!userId) {
      handleError(new Error('Missing user identifier for approval action.'), {
        component: 'AdminUsersPage',
        action: 'approvalAction',
      });
      return;
    }

    try {
      setApprovalProcessing(true);
      if (approvalDialog.action === 'APPROVE') {
        await adminUsersAPI.approveUser({
          user_id: String(userId),
          action: 'APPROVE',
        });
        setSuccessMessage('User approved successfully.');
      } else {
        await adminUsersAPI.rejectUser({
          user_id: String(userId),
          action: 'REJECT',
        });
        setSuccessMessage('User rejected successfully.');
      }
      closeApprovalDialog();
      await refresh();
    } catch (error) {
      handleError(error, {
        component: 'AdminUsersPage',
        action: approvalDialog.action === 'APPROVE' ? 'approveUser' : 'rejectUser',
      });
    } finally {
      setApprovalProcessing(false);
    }
  }, [approvalDialog, handleError, refresh]);

  const ensureBulkRoles = useCallback(async () => {
    if (bulkRoles.length > 0) {
      return bulkRoles;
    }
    const rolesResponse = await adminUsersAPI.getRoles();
    setBulkRoles(rolesResponse);
    return rolesResponse;
  }, [bulkRoles]);

  useEffect(() => {
    if (bulkDrawerOpen) {
      ensureBulkRoles().catch((error) => {
        console.error('[AdminUsersPage] Failed to preload roles for bulk upload', error);
      });
    }
  }, [bulkDrawerOpen, ensureBulkRoles]);

  const handleBulkFileSelect = useCallback((file: File) => {
    setBulkError(null);
    setBulkCsvRows([]);
    setBulkValidRows([]);
    setBulkInvalidRows([]);

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = reader.result as string;
        const rows = parseBulkCsv(text);
        if (rows.length === 0) {
          setBulkError('No records found in the uploaded file.');
        }
        setBulkCsvRows(rows);
      } catch (error) {
        setBulkError((error as Error).message);
      }
    };

    reader.onerror = () => {
      setBulkError('Unable to read the selected file.');
    };

    reader.readAsText(file);
  }, []);

  const handleBulkValidate = useCallback(async () => {
    if (bulkCsvRows.length === 0) {
      setBulkError('Please upload a CSV file before validating.');
      return;
    }

    try {
      setBulkProcessing(true);
      const roles = await ensureBulkRoles();
      // validateBulkRows is now async - checks both within-file and system duplicates
      const { valid, invalid, apiCheckFailed } = await validateBulkRows(bulkCsvRows, roles);
      setBulkValidRows(valid);
      setBulkInvalidRows(invalid);

      // Build error/warning message
      let errorMessage: string | null = null;
      if (invalid.length > 0) {
        errorMessage = 'Some records need attention.';
      }
      if (apiCheckFailed) {
        const apiWarning = 'System duplicate check could not be completed. Duplicates may still be caught on submission.';
        errorMessage = errorMessage ? `${errorMessage} ${apiWarning}` : apiWarning;
      }
      setBulkError(errorMessage);
    } catch (error) {
      setBulkError((error as Error).message);
    } finally {
      setBulkProcessing(false);
    }
  }, [bulkCsvRows, ensureBulkRoles]);

  const handleBulkSubmit = useCallback(async () => {
    if (bulkValidRows.length === 0) {
      setBulkError('Please validate and ensure there are valid records to submit.');
      return;
    }

    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      handleError(new Error('Unable to determine current user for bulk upload.'), {
        component: 'AdminUsersPage',
        action: 'bulkUploadUsers',
      });
      return;
    }

    try {
      setBulkProcessing(true);
      await adminUsersAPI.uploadBulkUsers({
        user_id: currentUserId,
        bulkusers: bulkValidRows.map((row) => ({
          ...row,
          purpose: row.purpose ?? [],
          channel_type: row.channel_type ?? [],
        })),
      });
      setSuccessMessage('Bulk user creation initiated successfully.');
      setBulkDrawerOpen(false);
      setBulkCsvRows([]);
      setBulkValidRows([]);
      setBulkInvalidRows([]);
      await refresh();
    } catch (error) {
      handleError(error, { component: 'AdminUsersPage', action: 'bulkUploadUsers' });
    } finally {
      setBulkProcessing(false);
    }
  }, [bulkValidRows, handleError, refresh]);

  const tablePagination = useMemo(
    () => ({
      page: pagination.page,
      pageSize: pagination.pageSize,
      totalItems: pagination.totalItems,
    }),
    [pagination]
  );

  // Valid users columns - matching Angular's displayedColumUserList (5 columns)
  const bulkSuccessColumns = useMemo<TableColumn<BulkUploadUserRow>[]>(
    () => [
      { key: 'email', header: 'Email', width: 220, render: (_v, row) => <span className="text-sm">{row.email || '-'}</span> },
      { key: 'mobile', header: 'Mobile', width: 140, render: (_v, row) => <span className="text-sm">{row.mobile || '-'}</span> },
      { key: 'emp_id', header: 'Employee ID', width: 140, render: (_v, row) => <span className="text-sm">{row.emp_id || '-'}</span> },
      { key: 'name', header: 'Employee Name', width: 180, render: (_v, row) => <span className="text-sm">{row.name || '-'}</span> },
      { key: 'user_role_id', header: 'Role', width: 120, render: (_v, row) => <span className="text-sm">{row.user_role_id || '-'}</span> },
    ],
    []
  );

  // Failed users columns - matching Angular's displayedColumnFiledUserList (9 columns)
  const bulkFailureColumns = useMemo<TableColumn<BulkUploadUserRow>[]>(
    () => [
      { key: 'email', header: 'Email', width: 200, render: (_v, row) => <span className="text-sm">{row.email || '-'}</span> },
      { key: 'mobile', header: 'Mobile', width: 120, render: (_v, row) => <span className="text-sm">{row.mobile || '-'}</span> },
      { key: 'emp_id', header: 'Employee ID', width: 120, render: (_v, row) => <span className="text-sm">{row.emp_id || '-'}</span> },
      { key: 'userStatus', header: 'User Status', width: 280, render: (_v, row) => <span className="text-sm text-red-600">{row.userStatus || '-'}</span> },
      { key: 'name', header: 'Employee Name', width: 160, render: (_v, row) => <span className="text-sm">{row.name || '-'}</span> },
      { key: 'demand_num', header: 'Demand Number', width: 140, render: (_v, row) => <span className="text-sm">{row.demand_num || '-'}</span> },
      { key: 'dept_owner', header: 'Dept Owner', width: 140, render: (_v, row) => <span className="text-sm">{row.dept_owner || '-'}</span> },
      { key: 'dept_code', header: 'Dept Code', width: 120, render: (_v, row) => <span className="text-sm">{row.dept_code || '-'}</span> },
      { key: 'email_business_owner', header: 'Email Business Owner', width: 200, render: (_v, row) => <span className="text-sm">{row.email_business_owner || '-'}</span> },
    ],
    []
  );

  const bulkSuccessSection = useMemo(() => {
    if (bulkValidRows.length === 0) {
      return undefined;
    }
    return {
      title: 'Validated Users',
      description: 'These records passed validation and are ready for submission.',
      rows: bulkValidRows,
      columns: bulkSuccessColumns,
    };
  }, [bulkValidRows, bulkSuccessColumns]);

  const bulkErrorSection = useMemo(() => {
    if (bulkInvalidRows.length === 0) {
      return undefined;
    }
    return {
      title: 'Rows Requiring Attention',
      description: 'Review the highlighted issues and fix them in the CSV before re-uploading.',
      rows: bulkInvalidRows,
      columns: bulkFailureColumns,
    };
  }, [bulkFailureColumns, bulkInvalidRows]);

  // Combined drawer open state for DrawerProvider
  const isAnyDrawerOpen = formDrawerOpen || bulkDrawerOpen;
  const handleDrawerOpenChange = useCallback((open: boolean) => {
    if (!open) {
      // When drawer closes via overlay click or escape, close whichever is open
      setFormDrawerOpen(false);
      setBulkDrawerOpen(false);
    }
  }, []);

  return (
    <DrawerProvider side="right" open={isAnyDrawerOpen} onOpenChange={handleDrawerOpenChange}>
      <div className="equal-ds-root h-full flex flex-col gap-4">
        {/* Header */}
        <header className="flex md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-text-primary tracking-wide">
              Admin Users
            </h1>
            <p className="text-sm text-text-secondary">
              Manage admin user accounts, permissions, and onboarding workflows.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={handleRefresh}
              disabled={refreshing || isLoading}
              title="Refresh"
              className="bg-white text-text-primary border border-border-default hover:bg-background-tertiary"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>

            {permissions.canBulkCreate && (
              <Button variant="secondary" onClick={() => setBulkDrawerOpen(true)}>
                <UploadCloud className="w-4 h-4" />
                Bulk Upload
              </Button>
            )}

            {permissions.canCreateUser && (
              <Button variant="primary" onClick={handleCreateClick}>
                <UserPlus className="w-4 h-4" />
                Create User
              </Button>
            )}

            {permissions.canDownloadCsv && (
              <Button
                variant="secondary"
                onClick={handleDownloadCsv}
                disabled={exportProcessing}
                className="bg-white text-text-primary border border-border-default hover:bg-background-tertiary"
              >
                <Download className="w-4 h-4" />
                <span className="ml-2">{exportProcessing ? 'Exporting…' : 'Export'}</span>
              </Button>
            )}
          </div>
        </header>

        {/* Alerts */}
        <div className="space-y-3">
          {errorMessage ? (
            <Alert variant="error" onClose={clearError}>
              {errorMessage}
            </Alert>
          ) : null}
          {successMessage ? (
            <Alert variant="success" onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          ) : null}
        </div>

        {/* Loading indicator for edit - positioned above table instead of full-screen overlay */}
        {editLoading && (
          <div className="bg-white rounded-lg p-3 shadow-md border border-border-default flex items-center gap-3 w-fit">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-text-primary">Loading user data...</span>
          </div>
        )}

        {/* Table */}
        <AdminUsersTable
          rows={rows}
          isLoading={isLoading || editLoading}
          pagination={tablePagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onEdit={handleEdit}
          onToggleStatus={handleStatusChange}
          onResetPassword={handleResetPassword}
          onApprove={handleApprove}
          onReject={handleReject}
          permissions={permissions}
          //onCreateRequested={permissions.canCreateUser ? handleCreateClick : undefined}
          searchForm={searchForm}
          onSearchFieldChange={setSearchField}
          onSearchValueChange={setSearchValue}
          onSearchSubmit={handleSearchSubmit}
          onSearchReset={handleSearchReset}
        />

        <AdminUserFormDrawer
          isOpen={formDrawerOpen}
          mode={formMode}
          editingUser={editingUser ?? undefined}
          onClose={() => setFormDrawerOpen(false)}
          onSubmit={handleFormSubmit}
          isSubmitting={formProcessing}
        />

        <AdminUsersBulkUploadDrawer
          isOpen={bulkDrawerOpen}
          onClose={() => setBulkDrawerOpen(false)}
          onDownloadSample={() => {
            // Use relative path (without leading slash) to work across all environments
            // Angular uses 'assets/user_list.csv' - we match that pattern
            window.open('assets/user_list.csv', '_blank');
          }}
          fileInput={{
            label: 'Select CSV',
            onFileSelect: handleBulkFileSelect,
            accept: '.csv',
            helperText: 'Use the sample template to ensure proper headers before uploading.',
            disabled: bulkProcessing,
          }}
          validateConfig={{
            label: 'Validate Sheet',
            onValidate: handleBulkValidate,
            disabled: bulkCsvRows.length === 0 || bulkProcessing,
          }}
          errorSection={bulkErrorSection}
          successSection={bulkSuccessSection}
          footerActions={{
            secondary: {
              label: 'Close',
              onClick: () => setBulkDrawerOpen(false),
              disabled: bulkProcessing,
            },
            primary: {
              label: 'Submit Users',
              onClick: handleBulkSubmit,
              disabled: bulkValidRows.length === 0 || bulkProcessing,
            },
          }}
          contentSlot={
            bulkError || bulkCsvRows.length > 0 ? (
              <div className="space-y-3">
                {bulkError ? (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {bulkError}
                  </div>
                ) : null}
                {bulkCsvRows.length > 0 ? (
                  <div className="text-sm text-text-secondary bg-background-tertiary border border-border-default rounded-lg px-3 py-2">
                    <div>Total rows detected: {bulkCsvRows.length}</div>
                    <div>Ready to submit: {bulkValidRows.length}</div>
                    <div>Needs attention: {bulkInvalidRows.length}</div>
                  </div>
                ) : null}
              </div>
            ) : null
          }
          isBusy={bulkProcessing}
        />

        <AdminUserStatusDialog
          user={statusDialog.user}
          nextStatus={statusDialog.nextStatus}
          isOpen={Boolean(statusDialog.user)}
          onClose={closeStatusDialog}
          onConfirm={handleStatusConfirm}
          isProcessing={statusProcessing}
        />

        <AdminUserResetPasswordDialog
          user={resetDialogUser}
          isOpen={Boolean(resetDialogUser)}
          onClose={closeResetDialog}
          onConfirm={handleResetPasswordConfirm}
          isProcessing={resetProcessing}
        />

        <AdminUserApprovalDialog
          user={approvalDialog.user}
          action={approvalDialog.action}
          isOpen={Boolean(approvalDialog.user)}
          onClose={closeApprovalDialog}
          onConfirm={handleApprovalConfirm}
          isProcessing={approvalProcessing}
        />

        <SignupRequestApprovalDialog
          request={signupApprovalDialog.request}
          mode={signupApprovalDialog.mode}
          isOpen={Boolean(signupApprovalDialog.request)}
          onClose={closeSignupApprovalDialog}
          onSuccess={() => {
            closeSignupApprovalDialog();
            refresh();
          }}
          onShowToast={setSuccessMessage}
        />
      </div>
    </DrawerProvider>
  );
};
